import type { QuickFillItem, QuickFillRowMode } from '../../../shared/types';
import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';

export interface PipeFamily {
  name: string;
  category: string;
  types: Array<{
    id?: string;
    name: string;
    parameterValues?: Array<{
      parameterName: string;
      value: any;
      isReadOnly?: boolean;
      isInstance?: boolean;
    }>;
  }>;
}

export interface PipeElement {
  elementId?: string;
  elementName?: string;
  familyName?: string;
  typeName?: string;
  typeId?: string;
  category?: string;
  parameterValues?: Array<{
    parameterId?: string;
    parameterName: string;
    value: any;
    isReadOnly?: boolean;
    isInstance?: boolean;
  }>;
}

export interface CheckedProperty {
  id: string;
  label: string;
  revitParameter?: string;
  checked: boolean;
  isInstance?: boolean;
}

export type ParameterBindingScope = 'instance' | 'type' | 'unknown';

export interface CheckedPropertyWithLookup extends CheckedProperty {
  normalizedRevitParameterName: string;
  bindingScopeHint?: ParameterBindingScope;
}

export interface ProjectVisibleType {
  name: string;
  familyName: string;
  typeId?: string;
}

export interface ProjectVisibleTypeIndex {
  byFamilyName: Map<string, ProjectVisibleType[]>;
  byTypeId: Map<string, ProjectVisibleType>;
}

export interface ProjectElementIndexes {
  instanceParamIndex: Map<string, Map<string, ResolvedParam>>;
  instanceTargetIndex: Map<string, Map<string, string[]>>;
  modelElementIdIndex: Map<string, string[]>;
  projectVisibleTypeIndex: ProjectVisibleTypeIndex;
  projectVisibleTypeCount: number;
  projectVisibleTypeIdSamples: Array<string | undefined>;
  projectVisibleRowSamples: string[];
  elementsWithPayload: number;
}

export interface RenderableFamilyType {
  name: string;
  familyName: string;
  familyType: PipeFamily['types'][number] | null;
  typeId?: string;
}

export interface AvailableParameterMeta {
  name?: string;
  isInstance?: boolean;
}

export interface AvailableParameterScopeEntry {
  instance: boolean;
  type: boolean;
}

export interface ResolvedParam {
  value: string;
  isReadOnly: boolean;
  hasParameter: boolean;
  bindingScope: ParameterBindingScope;
  instanceTargetElementIds?: string[];
}

export interface PropertyPopulationSummary {
  label: string;
  revitParameter?: string;
  populatedRows: number;
  rowsWithParameter: number;
  readOnlyRows: number;
  sampleValues: string[];
}

export type QuickFillDatasetCacheKey = `${QuickFillRowMode}:${QuickFillSnapshotShapeOption}`;

export interface QuickFillBuiltDatasetCacheEntry {
  rows: QuickFillItem[];
  currentRevitParameters: Record<string, string>;
}

export const EMPTY_RESOLVED: ResolvedParam = {
  value: '',
  isReadOnly: false,
  hasParameter: false,
  bindingScope: 'unknown',
};

export const normalize = (value?: string | null): string => (value || '').trim().toLowerCase();

export const isMeaningful = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

export const getPayloadBindingScope = (parameterValue: { isInstance?: boolean }): ParameterBindingScope =>
  parameterValue.isInstance === false ? 'type' : 'instance';

export const rowKey = (familyName: string, typeName: string | null): string =>
  `${normalize(familyName)}:::${normalize(typeName ?? '')}`;

export const stableTypeKey = (typeId?: string | null): string | null => {
  const normalized = normalize(typeId || '');
  return normalized ? `type-id:::${normalized}` : null;
};

export const buildElementParamMap = (
  element: PipeElement | null | undefined
): Map<string, ResolvedParam> => {
  const map = new Map<string, ResolvedParam>();
  if (!element || !Array.isArray(element.parameterValues)) {
    return map;
  }

  for (const pv of element.parameterValues) {
    if (!pv || !pv.parameterName) continue;
    const resolved: ResolvedParam = {
      value: isMeaningful(pv.value) ? String(pv.value) : '',
      isReadOnly: pv.isReadOnly === true,
      hasParameter: true,
      bindingScope: getPayloadBindingScope(pv),
    };
    const existing = map.get(pv.parameterName);
    if (existing?.bindingScope === 'instance' && resolved.bindingScope !== 'instance') {
      continue;
    }
    map.set(pv.parameterName, resolved);
  }

  return map;
};
